import { Admin, Resource, fetchUtils } from 'react-admin';
import { Route } from 'react-router-dom';
import simpleRestProvider from 'ra-data-simple-rest';
import EmailIcon from '@mui/icons-material/Email';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import HubIcon from '@mui/icons-material/Hub';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import TuneIcon from '@mui/icons-material/Tune';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { ViaOrderList, ViaOrderShow } from './via/ViaOrderList';
import { EmailList } from './email/EmailList';
import { EmailShow } from './email/EmailShow';
import { PreOrderList } from './pre-order/PreOrderList';
import { PreOrderShow } from './pre-order/PreOrderShow';
import { PreOrderCreate } from './pre-order/PreOrderCreate';
import { PreOrderCopy } from './pre-order/PreOrderCopy';
import { COPY_PATH } from './pre-order/preOrderCopyApi';
import { OrderList } from './order/OrderList';
import { OrderShow } from './order/OrderShow';
import { UserOfferList } from './admin/userOffers/UserOfferList';
import { UserOfferShow } from './admin/userOffers/UserOfferShow';
import { MY_FLEET_PATH, MyFleetBank } from './admin/userOffers/MyFleetBank';
import { CarList } from './admin/fleet/CarList';
import { CarShow } from './admin/fleet/CarShow';
import { ContractorList } from './admin/contractors/ContractorList';
import { ContractorShow } from './admin/contractors/ContractorShow';
import { DriverList } from './admin/drivers/DriverList';
import { DriverShow } from './admin/drivers/DriverShow';
import { CarrierList } from './admin/carriers/CarrierList';
import { CarrierShow } from './admin/carriers/CarrierShow';
import { UserList } from './admin/users/UserList';
import {
  UserCreate,
  UserEdit,
} from './admin/users/UserForm';
import { CredentialsList } from './admin/credentials/CredentialsList';
import { AuditLogList } from './admin/auditLog/AuditLogList';
import { SuggestionParameterList } from './admin/suggestionParameters/SuggestionParameterList';
import { FeatureFlagList } from './admin/featureFlags/FeatureFlagList';
import { ExecutionDepartmentList } from './admin/executionDepartment/ExecutionDepartmentList';
import { TruckBanCalendar } from './admin/truckBans/TruckBanCalendar';
import { TradeDashboard } from './admin/tradeDashboard/TradeDashboard';
import { TradeReasonList } from './admin/tradeReasons/TradeReasonList';
import { FuelSurchargeScreen } from './admin/fuelSurcharges/FuelSurchargeScreen';
import { RoleCenter } from './admin/roleCenter/RoleCenter';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import DashboardIcon from '@mui/icons-material/Dashboard';
import RuleIcon from '@mui/icons-material/Rule';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import { NotificationHistory } from './admin/notifications/NotificationHistory';
import { FtlNotesInbox } from './admin/ftlNotes/FtlNotesInbox';
import { IntakeDashboard } from './admin/intake/IntakeDashboard';
import { IntakeControlWindow } from './admin/intake/IntakeControlWindow';
import { CONTROL_PATH } from './admin/intake/intakeApi';
import { OrderChangeList } from './admin/orderChanges/OrderChangeList';
import { CsQueueScreen } from './admin/csWork/CsQueueScreen';
import { UsageAnalytics } from './admin/usage/UsageAnalytics';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FeedbackIcon from '@mui/icons-material/FeedbackOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { RoleDashboard } from './admin/ftlDashboards/RoleDashboard';
import { PlannerDashboard, TeamLeaderDashboard } from './admin/ftlDashboards/FtlDashboards';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import { PublicationQueue } from './admin/publications/PublicationQueue';
import { PublicationExceptionsList } from './admin/publications/PublicationExceptionsList';
import GppMaybeIcon from '@mui/icons-material/GppMaybeOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { CompanyList } from './admin/companies/CompanyList';
import { TrailerList } from './admin/fleet/TrailerList';
import { FleetSchedule } from './admin/fleet/FleetSchedule';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import RvHookupIcon from '@mui/icons-material/RvHookup';
import { authProvider } from './authProvider';
import { i18nProvider } from './i18n/i18nProvider';
import { CustomLayout } from './admin/CustomLayout';
import { listMemoryStore } from './admin/listMemory/listMemoryStore';
import { LoginPage } from './admin/LoginPage';
import { appTheme } from './theme';
import { capitalizeErrorMessage } from './shared/sentence';
import { recordApiResult } from './usage/usageRecorder';

const API = process.env.REACT_APP_NEST_API_URL || 'http://localhost:3001';

// Attach the JWT to every data request.
const httpClient = (url: string, options: fetchUtils.Options = {}) => {
  const token = localStorage.getItem('token');
  const headers = (options.headers as Headers) || new Headers({ Accept: 'application/json' });
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // Odmowa z API trafia do powiadomienia samodzielnie — od wielkiej litery
  // (07.10.2026, `shared/sentence.ts`); status i treść błędu bez zmian.
  // Pomiar użycia (S-22.10-02, pełny ślad 23.10.2026): działanie (POST/PUT/
  // PATCH/DELETE) z kodem i czasem, błąd odczytu z kodem — bez treści
  // żądania, odpowiedzi i błędu.
  const started = Date.now();
  const method = options.method ?? 'GET';
  return fetchUtils.fetchJson(url, { ...options, headers }).then(
    (res) => {
      recordApiResult(method, url, res.status, Date.now() - started);
      return res;
    },
    (e: unknown) => {
      recordApiResult(method, url, (e as { status?: unknown } | null)?.status, Date.now() - started);
      throw capitalizeErrorMessage(e);
    },
  );
};

const dataProvider = simpleRestProvider(API, httpClient);

function App() {
  return (
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider}
      i18nProvider={i18nProvider}
      layout={CustomLayout}
      // Magazyn react-admina (ostatnie filtry, sortowanie i liczba na stronie
      // list). Bez przełącznika `autoListFilters` — ten sam
      // `localStorageStore()` co domyślny; z nim klucze osobno dla każdego
      // konta, bez czyszczenia filtrów przy wylogowaniu (decyzja użytkownika
      // 19.10.2026, `admin/listMemory/listMemoryStore.ts`).
      store={listMemoryStore}
      // Ekran logowania w kolorach aplikacji zamiast ciemnego tła react-admina
      // (01.10.2026). Formularz i logowanie bez zmian — admin/LoginPage.tsx.
      loginPage={LoginPage}
      // Ekran startowy zależny od roli (makieta back office v2, A4,
      // 20.10.2026): planista FTL — swój pulpit za przełącznikiem, pozostali
      // (także Team Leader — decyzja użytkownika 20.10.2026; pulpit Team
      // Leadera ma w menu) — pulpit ogólny jak dotąd.
      dashboard={RoleDashboard}
      // Jasny motyw react-admina (ten sam, który <Admin> bierze bez tej
      // właściwości) z jedną poprawką: `color="text.secondary"` na Typography
      // znów daje szary — MUI 9 przestało to robić samo. Szczegóły: theme.ts.
      theme={appTheme}
      // Aplikacja chodzi wyłącznie w jasnym motywie.
      //
      // react-admin 5 domyślnie dostarcza motyw ciemny i wybiera go za
      // preferencją systemu operatora:
      //   prefersDarkMode && darkTheme ? 'dark' : 'light'
      // (ra-ui-materialui/dist/theme/ThemeProvider.js). Nikt tego u nas nie
      // wybierał — po prostu odziedziczyliśmy ustawienie Windowsa.
      //
      // Powód wyłączenia, a nie zostawienia wyboru: część naszych znaczników
      // ma kolory wpisane na sztywno pod jasne tło (statusZlecenia.ts —
      // pastelowe tła z ciemnym tekstem). Na ciemnym interfejsie odcinają się
      // jak wklejone z innej aplikacji. Zanim damy operatorowi przełącznik,
      // trzeba mieć paletę działającą w obu trybach — to osobna robota,
      // nie efekt uboczny tej zmiany.
      darkTheme={null}
    >
      {/* Bank zleceń — oś pracy planisty. Agreguje zlecenia z TMS klienta
          i prezlecenia (rozdział FTL §4), więc stoi na pierwszym miejscu. */}
      <Resource
        name="user-offers"
        options={{ label: 'Zlecenia' }}
        icon={InventoryIcon}
        list={UserOfferList}
        show={UserOfferShow}
      >
        {/* „Zlecenia mojej floty” (makieta FTL pkt 9.3, 15.10.2026) — ta sama
            lista ze stałym filtrem „moje”, w zasobie zleceń, więc wiersz
            prowadzi do zwykłej karty. Za przełącznikiem `bankMyFleetView`. */}
        <Route path={MY_FLEET_PATH} element={<MyFleetBank />} />
      </Resource>
      {/* Flota — kartoteka z TMS klienta, wyłącznie do odczytu. Stoi zaraz
          po banku, bo planista sięga po nią przy obsadzaniu zleceń. */}
      <Resource
        name="cars"
        options={{ label: 'Samochody' }}
        icon={LocalShippingIcon}
        list={CarList}
        show={CarShow}
      />
      {/* Harmonogram floty (29.09.2026) — auta w wierszach, dni
          w kolumnach, obsadzenia i warianty jako paski. Planiści pracują
          z listy aut, nie z listy zleceń. Tylko odczyt, dane z API
          `car-schedules/fleet`, nie z dostawcy danych react-admina. */}
      <Resource
        name="fleet-schedule"
        options={{ label: 'Harmonogram floty' }}
        icon={ViewTimelineIcon}
        list={FleetSchedule}
      />
      {/* Naczepy — widok na kartotekę pojazdów zawężony typem, bo klient
          nie prowadzi ich osobno (580 z 1212 rekordów to typ TRAILER).
          Szczegóły prowadzą do tej samej karty co flota. */}
      <Resource
        name="trailers"
        options={{ label: 'Naczepy' }}
        icon={RvHookupIcon}
        list={TrailerList}
      />
      {/* Kierowcy — kartoteka z TMS klienta, do odczytu. Stoi przy flocie,
          bo planista sprawdza tu, kto siedzi w aucie i w jakiej rotacji. */}
      <Resource
        name="drivers"
        options={{ label: 'Kierowcy' }}
        icon={BadgeIcon}
        list={DriverList}
        show={DriverShow}
      />
      {/* Konta użytkowników. Menu pokazuje tę pozycję wyłącznie rolom
          zarządzającym, ale o dostępie rozstrzyga API — strażnik uprawnień
          odrzuci żądanie także wtedy, gdy ktoś wpisze adres wprost. */}
      {/* Kolejka publikacji na giełdach (I.05). Wiersze powstają wyłącznie
          z decyzji człowieka przy zleceniu; tu widać je wszystkie razem,
          z wynikiem giełdy i założeniami, na jakich zbudowano ofertę. */}
      <Resource
        name="postings"
        options={{ label: 'Kolejka publikacji' }}
        icon={StorefrontIcon}
        list={PublicationQueue}
      />
      {/* Wnioski o wyjątek od bramki publikacji (makieta back office v2,
          fala B, 21.10.2026) — za przełącznikiem `publicationExceptionRequests`;
          wyłączony: pozycji w menu nie ma, adres pokazuje komunikat. */}
      <Resource
        name="publication-exceptions"
        options={{ label: 'Wnioski o wyjątek' }}
        icon={GppMaybeIcon}
        list={PublicationExceptionsList}
      />
      <Resource
        name="users"
        options={{ label: 'Użytkownicy' }}
        icon={PeopleIcon}
        list={UserList}
        create={UserCreate}
        edit={UserEdit}
      />
      {/* Poświadczenia integracji — wartości wyłącznie zaszyfrowane, bez
          możliwości podejrzenia. Patrz CredentialsList. */}
      {/* Spółki instancji — pozycja była w menu i w API, ale zasób nie był
          zarejestrowany, więc wejście kończyło się ekranem „Not Found”
          (wyszło przy przejściu po froncie 22.09.2026). Tylko odczyt. */}
      <Resource
        name="companies"
        options={{ label: 'Spółki' }}
        icon={BusinessIcon}
        list={CompanyList}
      />
      <Resource
        name="credentials"
        options={{ label: 'Poświadczenia' }}
        icon={VpnKeyIcon}
        list={CredentialsList}
      />
      {/* Dziennik działań administracyjnych — wyłącznie odczyt. Bez
          tworzenia i usuwania: dziennik, do którego da się dopisać albo
          z którego da się skasować, przestaje być dowodem. */}
      <Resource
        name="admin-log"
        options={{ label: 'Dziennik działań' }}
        icon={HistoryIcon}
        list={AuditLogList}
      />
      {/* Parametry podpowiadania aut (horyzont, okno pozycji, wiek GPS,
          domyślna ładowność) — konfiguracja z historią zmian i powodem.
          Zmianę rozstrzyga API: sekcja administracyjna. */}
      <Resource
        name="suggestion-parameters"
        options={{ label: 'Parametry rekomendacji' }}
        icon={TuneIcon}
        list={SuggestionParameterList}
      />
      {/* Powiadomienia działów z historią i filtrami (15.10.2026, Etap 1
          makiety FTL). Za przełącznikiem `notificationsPage` — wyłączony:
          pozycji w menu nie ma, a adres pokazuje komunikat. */}
      <Resource
        name="notifications"
        options={{ label: 'Powiadomienia' }}
        icon={NotificationsIcon}
        list={NotificationHistory}
      />
      {/* Pulpity FTL (makieta back office v2, A4, 20.10.2026) — za
          przełącznikami `plannerDashboard` i `teamLeaderDashboard`; liczy API
          tym samym filtrem co ekran, do którego prowadzi kafelek. */}
      <Resource
        name="ftl-planner-dashboard"
        options={{ label: 'Pulpit planisty' }}
        icon={SpaceDashboardIcon}
        list={PlannerDashboard}
      />
      <Resource
        name="ftl-team-leader-dashboard"
        options={{ label: 'Pulpit Team Leadera' }}
        icon={SpaceDashboardIcon}
        list={TeamLeaderDashboard}
      />
      {/* Przełączniki funkcji „do decyzji” z historią zmian i powodem
          (decyzja użytkownika 12.10.2026). Zmianę rozstrzyga API: sekcja
          administracyjna. */}
      <Resource
        name="feature-flags"
        options={{ label: 'Przełączniki funkcji' }}
        icon={ToggleOnIcon}
        list={FeatureFlagList}
      />
      {/* Kto realizuje wg TMS: flota czy Trade (odpowiedzi Marathonu/Euvic
          16.10.2026, pyt. 4a i 30). Za przełącznikiem
          `executionDepartmentHint` — wyłączony: pozycji w menu nie ma,
          a adres pokazuje komunikat. Zmiany rozstrzyga API: sekcja
          administracyjna. */}
      <Resource
        name="execution-department-mappings"
        options={{ label: 'Kto realizuje wg TMS' }}
        icon={AltRouteIcon}
        list={ExecutionDepartmentList}
      />
      {/* Kalendarz zakazów ruchu ciężarówek (odpowiedź Marathonu 16.10.2026,
          pyt. 11) — tylko odczyt. Za przełącznikiem `truckBanCalendar` —
          wyłączony: pozycji w menu nie ma, a adres pokazuje komunikat. */}
      <Resource
        name="truck-bans"
        options={{ label: 'Kalendarz zakazów' }}
        icon={EventBusyIcon}
        list={TruckBanCalendar}
      />
      {/* Powody przekazania do Trade i zwrotu (makieta back office v2, A6,
          20.10.2026) — za przełącznikiem `tradeTransferReasons`; zmiany
          rozstrzyga API: sekcja administracyjna. */}
      <Resource
        name="trade-transfer-reasons"
        options={{ label: 'Powody przekazania do Trade' }}
        icon={RuleIcon}
        list={TradeReasonList}
      />
      {/* Korekty paliwowe (makieta back office v2, fala B, B4, 21.10.2026) —
          za przełącznikiem `fuelSurcharges`; zmiany rozstrzyga API:
          uprawnienie „Przyjęcie zleceń”. */}
      <Resource
        name="fuel-surcharges"
        options={{ label: 'Korekty paliwowe' }}
        icon={LocalGasStationIcon}
        list={FuelSurchargeScreen}
      />
      {/* Pulpit Trade (makieta back office v2, A9, 20.10.2026) — za
          przełącznikiem `tradeDashboard`; liczy API tym samym filtrem co lista. */}
      <Resource
        name="trade-dashboard"
        options={{ label: 'Pulpit Trade' }}
        icon={DashboardIcon}
        list={TradeDashboard}
      />
      {/* Przewoźnicy — firmy spoza floty, które wożą zlecenia Marathona.
          Kartoteka powstaje z pola `carrier` przy zleceniu. */}
      <Resource
        name="carriers"
        options={{ label: 'Przewoźnicy' }}
        icon={HubIcon}
        list={CarrierList}
        show={CarrierShow}
      />
      {/* Kontrahenci — kartoteka z TMS klienta, do odczytu. Planista wchodzi
          tu ze zlecenia, żeby zobaczyć kontakty i historię współpracy. */}
      <Resource
        name="contractors"
        options={{ label: 'Kontrahenci' }}
        icon={BusinessIcon}
        list={ContractorList}
        show={ContractorShow}
      />
      <Resource
        name="emails"
        options={{ label: 'Skrzynka' }}
        icon={EmailIcon}
        list={EmailList}
        show={EmailShow}
      />
      <Resource
        name="pre-orders"
        options={{ label: 'Prezlecenia' }}
        icon={DescriptionIcon}
        list={PreOrderList}
        show={PreOrderShow}
        create={PreOrderCreate}
      >
        {/* Kopia z porównaniem pól (makieta back office v2, 20.10.2026) —
            za przełącznikiem `preOrderCopy`; wyłączony: komunikat. */}
        <Route path={COPY_PATH} element={<PreOrderCopy />} />
        {/* Okno kontroli (makieta back office v2, fala B, B1, 21.10.2026) —
            za przełącznikiem `intakeControlWindow`; wyłączony: komunikat. */}
        <Route path={CONTROL_PATH} element={<IntakeControlWindow />} />
      </Resource>
      <Resource
        name="orders"
        options={{ label: 'Zlecenia z maila' }}
        icon={AssignmentIcon}
        list={OrderList}
        show={OrderShow}
      />
      <Resource
        name="via-orders"
        options={{ label: 'Zlecenia VIA' }}
        icon={LocalShippingIcon}
        list={ViaOrderList}
        show={ViaOrderShow}
      />
      {/* „Uwagi od FTL” — lista odbiorcy (makieta back office v2, 20.10.2026).
          Za przełącznikiem `ftlNotes` — wyłączony: pozycji w menu nie ma,
          a adres pokazuje komunikat. Odbiorca: „Przyjęcie zleceń”. */}
      <Resource
        name="ftl-notes"
        options={{ label: 'Uwagi od FTL' }}
        icon={FeedbackIcon}
        list={FtlNotesInbox}
      />
      {/* Pulpit przyjęcia zleceń — kolejki CS i Administracji (makieta back
          office v2, fala B, B1, 21.10.2026). Za przełącznikiem
          `intakeDepartments`; liczy API tym samym filtrem co lista. */}
      <Resource
        name="intake-dashboard"
        options={{ label: 'Pulpit przyjęcia zleceń' }}
        icon={FactCheckIcon}
        list={IntakeDashboard}
      />
      {/* Zmiany zleceń „było → ma być” (fala B, B9, 21.10.2026) — za
          przełącznikiem `orderChangeRegister`; do TMS nic nie wychodzi. */}
      <Resource
        name="order-changes"
        options={{ label: 'Zmiany zleceń' }}
        icon={EditNoteIcon}
        list={OrderChangeList}
      />
      {/* Kolejka CS (fala C, 21.10.2026) — za przełącznikiem `csLight`;
          kolejność i krytyczność liczy API. */}
      <Resource
        name="cs-queue"
        options={{ label: 'Kolejka CS' }}
        icon={SupportAgentIcon}
        list={CsQueueScreen}
      />
      {/* Centrum ról i uprawnień (decyzja użytkownika 22.10.2026, S-22.10-01)
          — za przełącznikiem `roleCenter`, tylko administrator (API: 403
          każdemu innemu; ekran mówi to samo). */}
      <Resource
        name="role-center"
        options={{ label: 'Centrum ról i uprawnień' }}
        icon={ManageAccountsIcon}
        list={RoleCenter}
      />
      {/* Pomiar użycia (decyzja użytkownika 22.10.2026, S-22.10-02) — za
          przełącznikiem `usageTelemetry`, tylko rola ADMIN (rozstrzyga API). */}
      <Resource
        name="usage-analytics"
        options={{ label: 'Pomiar użycia' }}
        icon={QueryStatsIcon}
        list={UsageAnalytics}
      />
    </Admin>
  );
}

export default App;
